import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useStyles } from "./styles";
import {
  Grid,
  TextField,
  Typography,
  MenuItem
} from "@material-ui/core";
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
import { isEqual } from "lodash";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import clsx from "clsx";
import ContractService from "../../../../services/ContractService";
import RewardsService from "../../../../services/RewardsService";
import AccessGroupService from "../../../../services/AccessGroupService";
import apiClient from "../../../../auth/apiClient";
import * as c from "../../../../constants";
import ChargedPaymentMethodForm from "./ChargedPaymentMethodForm/ChargedPaymentMethodForm";
import CreditCardOnFilePaymentMethodForm from "./CreditCardOnFilePaymentMethodForm/CreditCardOnFilePaymentMethodForm";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlags";

const contractService = new ContractService(apiClient);
const rewardsService = new RewardsService(apiClient);
const accessGroupService = new AccessGroupService(apiClient);

const FlexibleParkingAccountForm = ({
  children,
  contractID,
  facilityID,
  disabled,
  paymentMethod,
  handlePayMentMethodChange,
  contractInfo,
  contractDetailsLoaded,
  updateContractInfo,
  entityID,
  accessHolderID,
  isOneParkUser,
  pointsBalance: pointsBalanceProp,
  onOpenPointsModal,
  assignedAccessHolderName,
}) => {
  const {
    flexibleParkingCreditCardOnFile: creditCardOnFileFlag
  } = useFlags();
  const rewardsEnabled = useFeatureFlag("ONEpark.Rewards");
  const [isPaymentMethodChange, setIsPaymentMethodChange] = useState(false);
  const [isCreatedOrUpdatedContract, setIsCreatedOrUpdatedContract] = useState(false);
  const classes = useStyles();
  const enqueueSnackbar = useEnqueueSnackbar();

 
  const [rewardGroups, setRewardGroups] = useState([]);
  const [selectedRewardGroupID, setSelectedRewardGroupID] = useState("");
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyRewardsEnabled, setLoyaltyRewardsEnabled] = useState(false);

  // Fetch access group settings to check if Loyalty Rewards is enabled
  useEffect(() => {
    if (!isOneParkUser || !accessHolderID) return;

    let cancelled = false;

    const checkLoyaltyRewardsEnabled = async () => {
      try {
        const response = await accessGroupService.GetAssignedAccessGroups(accessHolderID);
        if (!cancelled) {
          const groups = response?.data?.collection || response?.data || [];
          const hasLoyaltyEnabled = groups.some(
            (group) => group?.fpaSettings?.enableLoyaltyRewards === true
          );
          setLoyaltyRewardsEnabled(hasLoyaltyEnabled);
        }
      } catch {
        if (!cancelled) {
          setLoyaltyRewardsEnabled(false);
        }
      }
    };

    checkLoyaltyRewardsEnabled();
    return () => { cancelled = true; };
  }, [accessHolderID, isOneParkUser]);

  
  useEffect(() => {
    if (!isOneParkUser || !rewardsEnabled || !entityID || !accessHolderID) return;

    let cancelled = false;

    const fetchLoyaltyData = async () => {
      setLoyaltyLoading(true);

      
      try {
        const rewardGroupsResponse = await rewardsService.getRewardGroups(entityID);
        if (!cancelled) {
          const groups = rewardGroupsResponse?.data?.collection || [];
          groups.sort((a, b) =>
            (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
          );
          setRewardGroups(groups);
        }
      } catch {
        // Reward groups endpoint failed
      }

      try {
        const loyaltyResponse = await rewardsService.getAccessHolderLoyalty(entityID, accessHolderID);
        if (!cancelled) {
          const loyaltyData = loyaltyResponse?.data;
          setSelectedRewardGroupID(loyaltyData?.rewardGroupID || "");
        }
      } catch {
        if (!cancelled) {
          setSelectedRewardGroupID("");
        }
      }

      if (!cancelled) setLoyaltyLoading(false);
    };

    fetchLoyaltyData();

    return () => { cancelled = true; };
  }, [entityID, accessHolderID, isOneParkUser, rewardsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildRewardGroupMetaData = (newRewardGroupID) => {
    const rewardGroupName =
      rewardGroups.find((group) => group.rewardGroupID === newRewardGroupID)?.name ?? "";
    const firstName =
      typeof assignedAccessHolderName === "object" ? (assignedAccessHolderName?.first ?? "") : "";
    const lastName =
      typeof assignedAccessHolderName === "object" ? (assignedAccessHolderName?.last ?? "") : "";

    const changes = {
      accessHolderID,
      accessHolderName: `${firstName} ${lastName}`.trim(),
      rewardGroupName,
    };
    return JSON.stringify(changes);
  };

  const handleRewardGroupChange = async (event) => {
    const newRewardGroupID = event.target.value;
    if (newRewardGroupID === selectedRewardGroupID) return;

    const previousValue = selectedRewardGroupID;
    const metaData = buildRewardGroupMetaData(newRewardGroupID);
    setSelectedRewardGroupID(newRewardGroupID);
    setLoyaltyLoading(true);

    try {
      await rewardsService.updateRewardGroupAssignment(entityID, accessHolderID, newRewardGroupID, metaData);
    } catch {
      setSelectedRewardGroupID(previousValue);
      enqueueSnackbar("Failed to update reward group assignment", {
        variant: "error",
        tag: "rewardGroupAssignmentError",
      });
    } finally {
      setLoyaltyLoading(false);
    }
  };

  const formatCurrency = (obj) => {
    Object.entries(obj).forEach(([key, val]) => {
      console.dir(parseFloat(val));
      if (key == "paymentMethod") return;
      if (!isNaN(parseFloat(val))) {
        const nonPaddedVal = `${val}`;
        if (nonPaddedVal.includes(".")) {
          const [,afterDecimal] = nonPaddedVal.split(".");
          if (afterDecimal.length == 1) {
            obj[key] = nonPaddedVal + "0";
          } else if (afterDecimal.length == 0) {
            obj[key] = nonPaddedVal + "00";
          }
        } else {
          obj[key] = nonPaddedVal + ".00";
        }
      }
    });
  }                     

  const FPASchema = Yup.object().shape({
    paymentMethod: Yup.number()
      .oneOf([...Object.values(c.FPA_PAYMENT_METHODS)]),
    threshold: Yup.number()
      .typeError("Must be a number")
      .min(0, "Must be positive")
      .required("Required")
      .when("paymentMethod", {
        is: (paymentMethod) => !paymentMethodIsCharged(paymentMethod),
        then: Yup.number().notRequired(),
      }),
    autoRecharge: Yup.boolean()
      .when("paymentMethod", {
        is: (paymentMethod) => !paymentMethodIsCharged(paymentMethod),
        then: Yup.boolean().notRequired(),
      }),
    rechargeAmount: Yup.number().when("autoRecharge", {
      is: (autoRecharge) => autoRecharge,
      then: Yup.number()
        .typeError("Must be a number")
        .min(0, "Must be positive")
        .required("Required"),
      otherwise: Yup.number().nullable(),
    }).when("paymentMethod", {
      is: (paymentMethod) => !paymentMethodIsCharged(paymentMethod),
      then: Yup.number().notRequired(),
    }),
    showFee: Yup.boolean()
      .when("paymentMethod", {
        is: (paymentMethod) => !paymentMethodIsCharged(paymentMethod),
        then: Yup.boolean().notRequired(),
      }),
    showBalance: Yup.boolean(),
    allowNegative: Yup.boolean()
      .when("paymentMethod", {
        is: (paymentMethod) => !paymentMethodIsCharged(paymentMethod),
        then: Yup.boolean().notRequired(),
      }),
    negativeLimit: Yup.number().when("allowNegative", {
      is: (allowNegative) => allowNegative,
      then: Yup.number()
        .typeError("Must be a number")
        .min(0, "Enter as a positive number")
        .required("Required"),
      otherwise: Yup.number().nullable(),
    }).when("paymentMethod", {
      is: (paymentMethod) => !paymentMethodIsCharged(paymentMethod),
      then: Yup.number().nullable().optional(),
    }),
    emailReceipts: Yup.boolean().when("paymentMethod", {
      is: (paymentMethod) => paymentMethodIsCharged(paymentMethod),
      then: Yup.boolean().notRequired(),
    })
  });

  const defaultValues = {
    paymentMethod: c.FPA_PAYMENT_METHODS["CHARGE_CCOF"],
    threshold: "0.00",
    autoRecharge: false,
    rechargeAmount: "0.00",
    showFee: true,
    showBalance: false,
    allowNegative: false,
    negativeLimit: "0.00",
    emailReceipts: true
  };

  const paymentMethodIsCharged = (paymentMethodTmp) => {
    return (!paymentMethodTmp || paymentMethodTmp === c.FPA_PAYMENT_METHODS["CHARGE_FPA"])
  }

  // Initialize form with actual data from DB if available, otherwise use defaults
  // Only initialize after contractDetailsLoaded to prevent showing defaults first
  const getInitialValues = () => {
    if (!contractDetailsLoaded) {
      return defaultValues;
    }

    if (contractInfo?.payload) {
      let formDefaultValues = { ...contractInfo.payload };

      // Handle legacy data without credit card on file flag
      if (!creditCardOnFileFlag && !paymentMethodIsCharged(contractInfo?.payload.paymentMethod)) {
        formDefaultValues.paymentMethod = c.FPA_PAYMENT_METHODS["CHARGE_FPA"];
      }

      return formDefaultValues;
    }

    return defaultValues;
  };

  const methods = useForm({
    resolver: yupResolver(FPASchema),
    defaultValues: getInitialValues(),
    mode: "onChange",
  });

  const {
    control,
    resetField,
    watch,
    getValues,
    setValue,
    reset,
    formState: { isDirty, isValid, errors },
  } = methods;

  const createOrUpdateContractDetails = async (values) => {
    setIsCreatedOrUpdatedContract(true);

    const isCreateDetails = contractInfo?.contractDetailsID ? false : true;
    try {
      let response;

      if (isCreateDetails) {
        response = await contractService.createContractDetails(
          facilityID,
          contractID,
          c.CONTRACT_DETAILS_TYPE["Flexible Parking Account"],
          {
            payload: values,
            contractID: contractID,
            typeID: c.CONTRACT_DETAILS_TYPE["Flexible Parking Account"],
          }
        );
      } else {
        response = await contractService.updateContractDetails(
          facilityID,
          contractID,
          c.CONTRACT_DETAILS_TYPE["Flexible Parking Account"],
          {
            payload: values,
            contractID: contractID,
            typeID: c.CONTRACT_DETAILS_TYPE["Flexible Parking Account"],
          }
        );
      }

      if (response?.data?.payload) {
        updateContractInfo(response.data);
      }
    } catch {
      enqueueSnackbar(
        `Failed to  ${
          isCreateDetails ? "create" : "update"
        } flexible account details`,
        {
          variant: "error",
          tag: `${isCreateDetails ? "create" : "update"}ContractDetailsError`,
        }
      );
    }
  };

  const handleBlur = () => {
    const formValues = getValues();

    if (!creditCardOnFileFlag || paymentMethodIsCharged(paymentMethod) || isPaymentMethodChange) {
      formatCurrency(formValues);
      setValue("threshold", formValues.threshold);
      setValue("negativeLimit", formValues.negativeLimit);
      setValue("rechargeAmount", formValues.rechargeAmount);
      formValues.threshold = parseFloat(getValues("threshold"));
      formValues.negativeLimit = parseFloat(getValues("negativeLimit"));
      formValues.rechargeAmount = parseFloat(getValues("rechargeAmount"));
    }

    const currentValues = contractInfo?.payload ?? defaultValues;
    if ((isPaymentMethodChange && isValid && !isEqual(currentValues, formValues)) || (isDirty && isValid && !isEqual(currentValues, formValues))) {
      createOrUpdateContractDetails(formValues);
    }
  };

  const handlePayMentMethodChanges = (event) => {
    handlePayMentMethodChange(event);
    setValue("paymentMethod", event.target.value);
    setIsPaymentMethodChange(true);

    if(event.target.value === c.FPA_PAYMENT_METHODS["CHARGE_FPA"]) {
      const formValues = getValues();

      formatCurrency(formValues)
    }
  }

  // Reset form when contract details are loaded from DB
  // This handles updates after the form is already initialized
  useEffect(() => {
    if (!isCreatedOrUpdatedContract && contractDetailsLoaded && contractInfo?.payload) {
      if (!creditCardOnFileFlag || paymentMethodIsCharged(contractInfo?.payload.paymentMethod)) {
        formatCurrency(contractInfo?.payload);
      }

      let formDefaultValues = { ...contractInfo.payload };

      if (!creditCardOnFileFlag && !paymentMethodIsCharged(contractInfo?.payload.paymentMethod)) {
        formDefaultValues.paymentMethod = c.FPA_PAYMENT_METHODS["CHARGE_FPA"];
      }
      reset(formDefaultValues);
    }
  }, [contractInfo?.payload, contractDetailsLoaded, isCreatedOrUpdatedContract])

  // Auto-seed FPA defaults for access holders that have a contract but no
  // persisted FPA row yet (e.g. created via bulk import or the external API).
  // Runs once per contract, only after the details fetch has completed and
  // returned no payload. Existing holders already have contractInfo.payload,
  // so this is skipped for them.
  useEffect(() => {
    if (!contractDetailsLoaded) return;
    if (!contractID || !facilityID) return;
    if (contractInfo?.payload || isCreatedOrUpdatedContract) return;

    createOrUpdateContractDetails(defaultValues);
  }, [contractDetailsLoaded, contractID, facilityID, contractInfo?.payload, isCreatedOrUpdatedContract]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={clsx("card-content", classes.contentRoot)}>
      {/* Don't render form fields until contract details are loaded to prevent showing defaults first */}
      {contractDetailsLoaded && (
        <>
          {creditCardOnFileFlag && (
            <Grid container className={clsx("card-content", classes.payContainer)} spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={12} lg={7}>
                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      data-id="payment-method"
                      id="payment-method"
                      name="payment-method"
                      className={clsx(
                        "payType",
                        classes.payType,
                        classes.flexInput
                      )}
                      label={<Typography>Payment Method</Typography>}
                      select
                      disabled={disabled}
                      variant="outlined"
                      SelectProps={{
                        SelectDisplayProps: { "data-testid": "payment-method" }
                      }}
                      style={
                        { width: "100%" }
                      }
                      onChange={(e) =>
                        handlePayMentMethodChanges(e)
                      }
                      onBlur={handleBlur}
                      defaultValue={field.value ?? c.FPA_PAYMENT_METHODS["CHARGE_FPA"]}
                      value={field.value ?? c.FPA_PAYMENT_METHODS["CHARGE_FPA"]}
                      error={errors && errors["paymentMethod"] ? true : false}
                      helperText={
                        (errors && errors["paymentMethod"]) ||
                        "when exiting"
                      }
                    >
                      <MenuItem value={c.FPA_PAYMENT_METHODS["CHARGE_FPA"]}>
                        Charge Flexible Parking Account{" "}
                      </MenuItem>
                      <MenuItem value={c.FPA_PAYMENT_METHODS["CHARGE_CCOF"]}>
                        Charge Credit Card on File{" "}
                      </MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              {isOneParkUser && rewardsEnabled && loyaltyRewardsEnabled && (
                <Grid item xs={12} md={12} lg={5} className={classes.rewardsContainer}>
                  <TextField
                    data-id="rewards-group"
                    id="rewards-group"
                    label={<Typography>Rewards Group</Typography>}
                    select={rewardGroups.length > 1}
                    disabled={disabled || loyaltyLoading || rewardGroups.length <= 1}
                    variant="outlined"
                    fullWidth
                    className={clsx("rewards-group-field", classes.rewardsDropdown)}
                    value={
                      rewardGroups.length === 0 && !loyaltyLoading
                        ? "No rewards group available"
                        : rewardGroups.length === 1
                        ? rewardGroups[0].name
                        : selectedRewardGroupID
                    }
                    onChange={rewardGroups.length > 1 ? handleRewardGroupChange : undefined}
                    SelectProps={
                      rewardGroups.length > 1
                        ? { displayEmpty: true, SelectDisplayProps: { "data-testid": "rewards-group" } }
                        : undefined
                    }
                    inputProps={rewardGroups.length <= 1 ? { "data-testid": "rewards-group" } : undefined}
                  >
                    {rewardGroups.map((group) => (
                      <MenuItem key={group.rewardGroupID} value={group.rewardGroupID}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    data-id="points-balance"
                    id="points-balance"
                    label="Points"
                    variant="standard"
                    value={pointsBalanceProp ?? 0}
                    InputProps={{ readOnly: true }}
                    inputProps={{ "data-testid": "points-balance" }}
                    className={clsx(
                      "points-balance-field",
                      classes.pointsField,
                      disabled ? classes.pointsFieldDisabled : classes.pointsFieldClickable,
                    )}
                    onClick={!disabled ? onOpenPointsModal : undefined}
                  />
                </Grid>
              )}
            </Grid>
          )}

          {(!creditCardOnFileFlag || !paymentMethod || paymentMethod === c.FPA_PAYMENT_METHODS["CHARGE_FPA"]) && (
            <ChargedPaymentMethodForm
              disabled={disabled}
              formControl={control}
              watch={watch}
              resetField={resetField}
              setValue={setValue}
              errors={errors}
              handleBlur={handleBlur}
            />
          )}
          {creditCardOnFileFlag && paymentMethod === c.FPA_PAYMENT_METHODS["CHARGE_CCOF"] && (
            <CreditCardOnFilePaymentMethodForm
              disabled={disabled}
              formControl={control}
              watch={watch}
              resetField={resetField}
              handleBlur={handleBlur}
            />
          )}
          {children}
        </>
      )}
    </div>
  );
};

FlexibleParkingAccountForm.propTypes = {
  children: PropTypes.node,
  contractID: PropTypes.string,
  contractDetailsLoaded: PropTypes.bool,
  facilityID: PropTypes.string,
  entityID: PropTypes.string,
  accessHolderID: PropTypes.string,
  isOneParkUser: PropTypes.bool,
  pointsBalance: PropTypes.number,
  onOpenPointsModal: PropTypes.func,
  assignedAccessHolderName: PropTypes.shape({
    first: PropTypes.string,
    last: PropTypes.string,
  }),
};

export default FlexibleParkingAccountForm;
 